import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { ChoiceGroup } from '@apps/card/routes/CardCustomizer';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { StationeryProductSelector } from '../ProductDetailsPage.types';
import { useSaleColor } from '../../DesignsGallery/utils/useSaleColor';
import { useProductToUIMapping } from '@apps/card/components/ProductUIConfig/useProductToUIMapping';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { useGetDiscountByVendor } from '../../DesignsGallery/components/GalleryItem/hooks/useGetDiscountByVendor';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import type { Product } from '@apps/card/components/ProductUIConfig/product.types';
import { useTranslation } from '@shared/core';
import { getOrientation } from '@apps/card/utils';
import { formatProductSizeTitle, isPostcard, useCardSizeLabels } from '@apps/card/utils/cardSizeName';
import { usePrintPrices } from '@apps/card/hooks/usePrintPrices';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { convertProductTypeToFormatParam } from '../utils';
import { usePdpColorways } from '../PdpColorwayContext';
import { useStationeryTemplatesForProductSizePickerQuery } from './ProductSizeSelector.graphql.generated';

const DiscountedPriceText = ({ originalPrice, newPrice }: { originalPrice: string; newPrice: string }) => {
  const saleColor = useSaleColor();
  const { tq } = useTranslation('stationery');
  return (
    <TextV2>
      <TextV2
        as="span"
        typographyVariant="body3"
      >
        {tq`From`}
      </TextV2>{' '}
      <TextV2
        as="span"
        textDecoration="line-through"
        color="mono10"
        typographyVariant="body3"
      >
        {originalPrice}
      </TextV2>{' '}
      <TextV2
        as="span"
        color={saleColor}
        fontWeight={600 as const}
        typographyVariant="body3"
      >
        {newPrice}
      </TextV2>
    </TextV2>
  );
};

const ActionItem = ({ selected, title, price }: { selected: boolean; title: string; price?: string | ReactElement }) => {
  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      flex={1}
      paddingX={3}
    >
      <TextV2
        typographyVariant="body1"
        color="mono12"
        fontWeight={selected ? 600 : 400}
      >
        {title}
      </TextV2>
      {typeof price === 'string' ? (
        <TextV2
          typographyVariant="body3"
          color="mono12"
          fontWeight={selected ? 600 : 400}
        >
          {price}
        </TextV2>
      ) : (
        price
      )}
    </Flex>
  );
};

// One row of the combined picker: a concrete product on a concrete template.
// Options on the current template switch the product in place; options on
// another template navigate the PDP to it (that is what a size change is).
type ProductSizeOption = Readonly<{
  key: string;
  templateId: string;
  product: StationeryProduct & Product;
  widthInches: number;
  heightInches: number;
}>;

const isOption = (input: ProductSizeOption | null): input is ProductSizeOption => !!input;

export const ProductSizeSelector = (props: { productSelector: StationeryProductSelector; templateCategory: StationeryTemplateCategoryEnum; themeId: string }) => {
  const { productSelector, templateCategory, themeId } = props;
  const { currentTemplate } = productSelector;
  const { getProductWithUIMapping } = useProductToUIMapping();
  const { products: currentProducts, activeProduct } = useStationeryProductPricingContext();
  const sizeLabels = useCardSizeLabels();
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  const { getDiscountByVendor } = useGetDiscountByVendor();
  const { buttonInteracted } = useCardTelemetry({ page: 'productPdpModal' });
  const { goToProductDetailsPage } = useCardsRouterContext();
  const { selectedColorwayId } = usePdpColorways();
  const { tq } = useTranslation('stationery');

  const { data, loading } = useStationeryTemplatesForProductSizePickerQuery({
    variables: {
      filter: {
        categories: [templateCategory],
        enabled: true,
        side: 'front',
        themeIds: [themeId],
      },
    },
    batchMode: 'fast',
    skip: !currentTemplate,
    // The size options only serve the interactive picker — no SEO value, so
    // don't block server rendering on them.
    ssr: false,
  });

  const options = useMemo<ProductSizeOption[]>(() => {
    const toOption = (template: { id: string; widthInches: number; heightInches: number }, product: StationeryProduct): ProductSizeOption | null => {
      const mapped = getProductWithUIMapping(product);
      if (!mapped) return null;
      return {
        key: `${template.id}:${product.id}`,
        templateId: template.id,
        product: mapped,
        widthInches: template.widthInches,
        heightInches: template.heightInches,
      };
    };

    // Digital products have no physical size, so they are single options pinned
    // to the current template. They come from the pricing context rather than
    // the size query because the parent already applied product visibility
    // rules there (free-digital dummy, basicDigitalV2 kill switch).
    const unsortedDigitalOptions = currentProducts
      .map(p => toOption({ id: currentTemplate?.id ?? '', widthInches: 0, heightInches: 0 }, p))
      .filter(isOption)
      .filter(o => !o.product.cardTypeSKU);
    // Premium Digital sits above Basic Digital (the free dummy or basicDigitalV2),
    // so the free option is always the very last row of the picker.
    const digitalOptions = [
      ...unsortedDigitalOptions.filter(o => o.product.productType === 'digitalCard'),
      ...unsortedDigitalOptions.filter(o => o.product.productType !== 'digitalCard'),
    ];

    if (!currentTemplate) return digitalOptions;

    const orientation = getOrientation(currentTemplate.widthInches, currentTemplate.heightInches);
    const sizeTemplates = (data?.stationeryTemplatesContainer.pagedResults.edges.map(edge => edge.node) ?? [])
      // Orientation has its own picker; the size axis stays within the current one.
      .filter(t => orientation === getOrientation(t.widthInches, t.heightInches));
    // The current template can be missing from the enabled-only list (e.g. a
    // direct link to a disabled template) — fall back to its own products so
    // the control still renders the selection.
    if (!sizeTemplates.some(t => t.id === currentTemplate.id)) {
      sizeTemplates.push({ ...currentTemplate, products: currentProducts });
    }
    sizeTemplates.sort((a, b) => a.widthInches - b.widthInches || a.heightInches - b.heightInches);

    const sizedOptions = sizeTemplates.flatMap(t =>
      t.products
        .map(p => toOption(t, p))
        .filter(isOption)
        .filter(o => !!o.product.cardTypeSKU),
    );

    // One fixed order on every category, so the picker never reshuffles as a
    // couple moves between PDPs: classic paper sizes, postcard, magnet, then
    // the digital variants together at the bottom (Premium above Basic). Within
    // a block, sizes stay ascending.
    const magnetOptions = sizedOptions.filter(o => o.product.productType === 'magnet');
    const classicPaperOptions = sizedOptions.filter(o => o.product.productType !== 'magnet' && !isPostcard(templateCategory, o.widthInches, o.heightInches));
    const postcardOptions = sizedOptions.filter(o => o.product.productType !== 'magnet' && isPostcard(templateCategory, o.widthInches, o.heightInches));
    return [...classicPaperOptions, ...postcardOptions, ...magnetOptions, ...digitalOptions];
  }, [currentProducts, currentTemplate, data, getProductWithUIMapping, templateCategory]);

  // Options can span templates beyond the current one, so pricing needs a
  // calculator over every option's product, not the context's (current
  // template only) calculator.
  const optionProducts = useMemo(() => {
    const byId = new Map<string, StationeryProduct>();
    options.forEach(o => byId.set(o.product.id, o.product));
    return [...byId.values()];
  }, [options]);
  const priceCalculator = usePrintPrices(optionProducts);

  const currentKey = `${currentTemplate?.id ?? ''}:${activeProduct.id}`;

  const getOptionTitle = (option: ProductSizeOption): string => formatProductSizeTitle(templateCategory, option.widthInches, option.heightInches, sizeLabels, option.product);

  const handleSelect = (key: string) => {
    const option = options.find(o => o.key === key);
    if (!option || key === currentKey) return;
    if (!currentTemplate || option.templateId === currentTemplate.id) {
      // Same template: a plain product switch, handled (and instrumented) by the parent.
      productSelector.onChange(option.product.id);
      return;
    }
    buttonInteracted({
      name: 'pdpModal_sizeControlClicked',
      themeId,
      stationeryTemplateCategory: templateCategory,
      fromTemplateId: currentTemplate.id,
      toTemplateId: option.templateId,
      fromCardWidthInches: currentTemplate.widthInches,
      fromCardHeightInches: currentTemplate.heightInches,
      toCardWidthInches: option.widthInches,
      toCardHeightInches: option.heightInches,
    });
    if (option.product.productType !== activeProduct.productType) {
      buttonInteracted({ name: 'pdpModal_formatControlClicked', themeId, format: option.product.id });
    }
    // Carry the colorway too — the navigation replaces the query string, so an
    // omitted param resets the couple's selection.
    goToProductDetailsPage('wedding', templateCategory, themeId, option.templateId, 'replace', convertProductTypeToFormatParam(option.product.productType), selectedColorwayId);
  };

  if (loading && !data && currentTemplate) {
    return (
      <Box marginBottom={7}>
        <ChoiceGroup.Skeleton
          choices={3}
          columns={1}
          minHeight={55}
        />
      </Box>
    );
  }

  if (options.length === 0) {
    return null;
  }

  return (
    <Box marginBottom={7}>
      <ChoiceGroup
        title={tq`Product`}
        // The selected row already reads as chosen; repeating its label next to
        // the title is noise — except with a single option, where the hidden
        // choice list leaves the eyebrow as the only place the product shows.
        hideValueFromTitle={options.length > 1}
        getName={(key: string) => {
          const option = options.find(o => o.key === key);
          return option ? getOptionTitle(option) : options.length === 1 ? getOptionTitle(options[0]) : '';
        }}
        value={currentKey}
        minHeight={55}
        choices={options.map(o => o.key)}
        setValue={handleSelect}
        columns={1}
        textTransform="none" // size measurements keep their lowercase reading
      >
        {options.map(option => {
          const { product } = option;
          const discount = getDiscountByVendor(product.vendor);
          const priceData = priceCalculator.getProductBasePrice(product.id, product.defaultQuantity, discount, true);
          const priceDataDiscounts = priceData.discounts;
          const unitPriceString = minorUnitsToPriceString(priceData.unitPriceInMinorUnits, { canShowFreeLabel: true });
          const preDiscountUnitPriceString = priceDataDiscounts && minorUnitsToPriceString(priceDataDiscounts.preDiscountedUnitPriceInMinorUnits, { canShowFreeLabel: true });

          const isDiscountDifferent = preDiscountUnitPriceString !== unitPriceString;
          const canDisplayDiscount = discount && preDiscountUnitPriceString && isDiscountDifferent;

          const priceDisplay = (() => {
            if (canDisplayDiscount) {
              return (
                <DiscountedPriceText
                  originalPrice={preDiscountUnitPriceString}
                  newPrice={unitPriceString}
                />
              );
            }

            return priceData.unitPriceInMinorUnits === 0
              ? unitPriceString // Expected to be "FREE" or a translation of it.
              : tq`From ${unitPriceString}`;
          })();

          return (
            <ActionItem
              key={option.key}
              selected={option.key === currentKey}
              title={getOptionTitle(option)}
              price={priceDisplay}
            />
          );
        })}
      </ChoiceGroup>
    </Box>
  );
};
